import React, { useState, useEffect, useRef } from "react";
import { Route, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import PosMainScreen from "./PosMainScreen";
import { useCart } from "../Context/CartContext";
import { useItemSearch } from "../Context/ItemSearchContext";
import { useDiscount } from "../Context/DiscountContext";
import { useOrderSession } from "../Context/OrderSessionContext";
import { clearInvoice } from "../actions/invoiceActions";

const POS_PATHS = [
  "/PosMainScreen",
  "/PosMainScreen/payment",
  "/edit/invoice/:id",
  "/edit/invoice/:id/payment",
];

const isNewSalePath = (path) => path === "/PosMainScreen";
const isNewSalePaymentPath = (path) => path === "/PosMainScreen/payment";

// PosMainScreen (item grid, cart, ...) is the heaviest screen in the app.
// The shared route-transition machinery in App.js normally tears down and
// rebuilds a page's whole component tree on every navigation - fine for
// most pages, but on POS that meant every single visit paid the full cost
// of re-rendering the entire item catalogue from scratch, even when
// returning to a POS session you'd only just left. Keeping it mounted after
// its first visit and only toggling visibility makes return trips instant.
// `isActive` is threaded down so PosMainScreen can still refresh its own
// data on each visit (see its effect) and so background-only side effects
// (ItemCards' barcode-scanner listener) don't stay live while hidden behind
// another page.
function KeepAlive({ match }) {
  const isActive = !!match;
  const [hasMounted, setHasMounted] = useState(isActive);
  const wasActiveRef = useRef(isActive);
  const { pathname } = useLocation();
  const prevPathnameRef = useRef(pathname);
  const { setSearchQuery } = useItemSearch();
  const {
    setCartItems,
    clearInvoiceStockCredit,
    setReceivedAmount,
    setVat,
  } = useCart();
  const { clearDiscount } = useDiscount();
  const { clearActiveSession } = useOrderSession();
  const dispatch = useDispatch();

  // Cart/session/discount live in React context (not localStorage), and this
  // tree stays mounted while hidden. Leaving POS or opening a fresh new-sale
  // screen must wipe that in-memory state or the previous cart comes back.
  const resetNewSale = () => {
    setSearchQuery("");
    setCartItems([]);
    setReceivedAmount?.("");
    setVat?.(undefined);
    clearInvoiceStockCredit?.();
    clearDiscount?.();
    clearActiveSession?.();
    dispatch(clearInvoice());
  };

  useEffect(() => {
    if (isActive) {
      setHasMounted(true);
    }

    const leftPos = wasActiveRef.current && !isActive;
    const prevPath = prevPathnameRef.current;
    const arrivedAtNewSale =
      isActive &&
      isNewSalePath(pathname) &&
      !isNewSalePath(prevPath) &&
      !isNewSalePaymentPath(prevPath);

    if (leftPos || arrivedAtNewSale) {
      resetNewSale();
    }

    wasActiveRef.current = isActive;
    prevPathnameRef.current = pathname;
    // resetNewSale reads stable context setters + dispatch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, pathname]);

  if (!hasMounted) return null;

  return (
    <div style={{ display: isActive ? undefined : "none" }}>
      <PosMainScreen isActive={isActive} />
    </div>
  );
}

export default function PersistentPosMainScreen({ user }) {
  if (!user) return null;
  return (
    <Route path={POS_PATHS} exact>
      {({ match }) => <KeepAlive match={match} />}
    </Route>
  );
}
